import type { MissionStatusEnum } from '@gamanza-engage/web-client-sdk';
import type { GameBase } from '../../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import type { Currency } from '../../../_ui-core_/base/package/api/package/types';
import type { ClubThousand, FeaturedGame, HeroBanner, Story } from './cms/package/cms-domain';
import type { RouteGuard } from './router/router-types/route-guard-types';
import type { GameEventTrackingWinType } from './service/tracking/tracking-service';

export type BuildInfo = {
  readonly commitHash: string;
  readonly timestamp: string;
  readonly version: string;
};

export type AppConfigRaw = {
  authProvider: 'tipico' | '5g';
  readonly abTastyId: string;
  readonly accountUrl: string;
  readonly androidSmartBannerId: string;
  readonly iosSmartBannerId: string;
  readonly apiUrl_casino: string;
  readonly apiUrl_feed_activity: string;
  readonly apiUrl_feed_general: string;
  readonly apiUrl_gaming: string;
  readonly apiUrl_pam: string;
  readonly canonicalOrigin: string;
  readonly contentUrl: string;
  /**
   * Cornucopia payment MFE bundle URLs (IGM-4082). The app resolves the bundles
   * through the static import map in `index.html`, and enablement is
   * `CasinoConfig.cornucopiaEnabled`. Reserved for the payment maintenance
   * completeness check (IGM-4160): flag on and any URL empty means maintenance.
   *
   * All four are required together: `payments-eligibility` carries the
   * regulatory popup catalog and `payment-limits` the limit-hit path, so a
   * cashier running without them would be uncompliant.
   */
  readonly cornucopiaPaymentsDepositUrl: string;
  readonly cornucopiaPaymentsEligibilityUrl: string;
  readonly cornucopiaPaymentsWithdrawalUrl: string;
  readonly cornucopiaPaymentLimitsUrl: string;
  readonly currency: string;
  readonly environment: string;
  readonly gamanzaClientId: string;
  readonly gamanzaServiceUrl: string;
  readonly helpCenterSettingsId: string;
  readonly helpCenterSrcUrl: string;
  readonly helpCenterUrl: string;
  readonly helpCenterIframeUrl: string;
  readonly label: 'games' | 'casino';
  readonly lang: string;
  readonly languages: string;
  readonly metaDescription: string;
  readonly metaRobotsDefault: RobotsContent;
  readonly metaTitle: string;
  readonly mockAuthUrl: string;
  readonly paymentUrl: string;
  readonly sentryDSN: string;
  readonly sentryGlobalHandlersIntegration: string;
  readonly sentrySampleRate: string;
  readonly sentryTracesSampleRate: string;
  readonly settingsUrl: string;
  readonly gamesUrl: string;
  readonly sportsUrl: string;
  readonly state: string;
  readonly staticUiHostAssetsUrl: string;
  readonly storageUrl: string;
  readonly theme: string;
  readonly verificationUrl: string;
};

export type AppConfig = {
  authProvider: 'tipico' | '5g';
  iOS26FixEnabled: boolean;
  paymentTeaserItems: PaymentTeaserItemProp[];
  prerenderMode: boolean;
  productSwitcherItems: ProductSwitcherItemProp[];
  readonly abTastyActive: boolean;
  readonly accountUrl: string;
  readonly androidSmartBannerId: string;
  readonly iosSmartBannerId: string;
  readonly apiUrl_casino: string;
  readonly apiUrl_feed_activity: string;
  readonly apiUrl_feed_general: string;
  readonly apiUrl_gaming: string;
  readonly apiUrl_pam: string;
  readonly canonicalOrigin: string;
  readonly contentUrl: string;
  /**
   * Cornucopia payment MFE bundle URLs (IGM-4082), carried through from
   * `AppConfigRaw`. Read by the payment maintenance completeness check
   * (IGM-4160): with `CasinoConfig.cornucopiaEnabled` on, any empty URL forces
   * maintenance — a partial stack cannot take payments compliantly.
   */
  readonly cornucopiaPaymentsDepositUrl: string;
  readonly cornucopiaPaymentsEligibilityUrl: string;
  readonly cornucopiaPaymentsWithdrawalUrl: string;
  readonly cornucopiaPaymentLimitsUrl: string;
  readonly currency: Currency;
  readonly environment: string;
  readonly gamanzaClientId: string;
  readonly gamanzaServiceUrl: string;
  readonly helpCenterSettingsId: string;
  readonly helpCenterSrcUrl: string;
  readonly helpCenterUrl: string;
  readonly helpCenterIframeUrl: string;
  readonly label: 'games' | 'casino';
  readonly lang: string;
  readonly languages: string[];
  readonly metaDescription: string;
  readonly metaRobotsDefault: RobotsContent;
  readonly metaTitle: string;
  readonly mockAuthUrl: string;
  readonly paymentUrl: string;
  readonly sentryDSN: string;
  readonly sentryGlobalHandlersIntegration: boolean;
  readonly sentrySampleRate: number;
  readonly sentryTracesSampleRate: number;
  readonly settingsUrl: string;
  readonly gamesUrl: string;
  readonly sportsUrl: string;
  readonly state: string;
  readonly staticUiHostAssetsUrl: string;
  readonly storageUrl: string;
  readonly theme: string;
  readonly verificationUrl: string;
};

export type RobotsContent = 'all' | 'noindex' | 'nofollow' | 'none' | '';

export type ProductSwitcherItemProp = {
  icon: string;
  text: string;
  showDownloadIcon: boolean;
  link: string;
};

export const ProductSwitcherIconName = {
  TIPICO_SPORTS: 'tipicoSports',
  TIPICO_GAMES: 'tipicoGames',
} as const;

export type ProductSwitcherIconName = (typeof ProductSwitcherIconName)[keyof typeof ProductSwitcherIconName];

export const PaymentTeaserIconName = {
  PAYPAL: 'paypal',
  KLARNA: 'klarna',
  PAYSAFECARD: 'paysafecard',
  MASTERCARD: 'mastercard',
  VISA: 'visa',
  TRUSTLY: 'trustly',
  SKRILL: 'skrill',
  SKRILL_ONE_TAP: 'skrillOneTap',
  APPLEPAY: 'applepay',
  GOOGLEPAY: 'googlepay',
} as const;

export type PaymentTeaserIconName = (typeof PaymentTeaserIconName)[keyof typeof PaymentTeaserIconName];

export type PaymentTeaserItemProp = {
  icon: PaymentTeaserIconName;
  title: string;
  description: string;
};

export interface AppSettings {
  // from config
  maintenanceActive: string;
  // set by concrete Application (NJ,DE)
  allowMultiGamePlay: boolean;
  dateFormattingOptions: Intl.DateTimeFormatOptions;
  dateTimeFormattingOptions: Intl.DateTimeFormatOptions;
  displayStateDisclaimer: string; // 'true' | 'false' | ''
  inactivityModal: InactivityOptions;
  isRegistrationEnabled?: boolean;
  isSHCasino?: boolean;
  keepAliveTimeoutMS: number;
  localeFormat: 'en-US' | 'de-DE';
  minSearchTermLength: number;
  panicButtonTTL: number;
  pastBonusLoadMoreInitialOffset: number;
  pastBonusLoadMoreStep: number;
  pastIncentiveBundlesLimit: number;
  realMoneyGamingMWebEnabled: boolean;
  shouldShowLocationModal?: boolean;
  supportFavorites: boolean;
  timeZoneOffsetM: number;
}

export type AppData = {
  casinoConfig: string;
};

export type InactivityOptions = {
  inactivityThresholdS: number;
  checkIntervalS: number;
};

// -------------------------------

export const Layout = {
  Mobile: 'mobile',
  Desktop: 'desktop',
} as const;

export type Layout = (typeof Layout)[keyof typeof Layout];

export type PopupActive = boolean;

export type VisibilityHidden = boolean;

export type MaintenanceConfig = {
  active: boolean;
};

export type TrackingStore = {
  gameCategory?: string;
  gameFilters?: string;
  gameName?: string;
  gamePosition?: number;
  gamePromo?: string;
  gameProvider?: string;
  gameSource?: string;
  winType?: GameEventTrackingWinType;
};

export type DevLobbyEntry = {
  id: string;
  name: string;
  default: boolean;
  selected: boolean;
};

export type CasinoSettings = {
  maintenance: MaintenanceConfig;
  lobbies: Array<DevLobbyEntry>;
  isDev?: boolean;
};

export type IncentiveMissionInfo = {
  id: string;
  title: string;
  rewardType: IncentiveBonusType;
  rewardAmount: number;
  missionAmount: number;
  missionUsed: number;
  isOptedIn: boolean;
  status: IncentiveMissionStatus;
  freeSpinValue: number;
  startsOn: Date;
  endsOn: Date;
  progress: number;
  steps: IncentiveMissionStep[];
  icon?: string;
  bgColor?: string;
};

export type IncentiveMissionStep = {
  status: MissionStatusEnum;
};

export type IncentiveStepMissionDetails = {
  title: string;
  reward: string;
  stage: number;
  games: GameBase[];
  stageStatus: IncentiveMultiStepMissionStatus;
  missionAmount: number;
  missionUsed: number;
  progress: number;
};

export const IncentiveMultiStepMissionStatus = {
  LOCKED: 'LOCKED',
  ACTIVE: 'ACTIVE',
  AVAILABLE: 'AVAILABLE',
} as const;

export type IncentiveMultiStepMissionStatus =
  (typeof IncentiveMultiStepMissionStatus)[keyof typeof IncentiveMultiStepMissionStatus];

export const IncentiveMissionStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  ENDED: 'ended',
} as const;

export type IncentiveMissionStatus = (typeof IncentiveMissionStatus)[keyof typeof IncentiveMissionStatus];

/* lets get rid from enums gradually */
export const IncentiveBonusType = {
  BONUS_MONEY: 'BM',
  FREE_SPINS: 'FS',
  XP: 'XP',
} as const;

export type IncentiveBonusType = (typeof IncentiveBonusType)[keyof typeof IncentiveBonusType];

export type IncentiveRewardType = 'xp' | 'MONEY' | 'FREE_SPIN';

export class AppContent {
  readonly featuredGame?: FeaturedGame;
  readonly heroBanner?: HeroBanner;
  readonly stories?: Story[];
  readonly clubThousand?: ClubThousand[];
}

export type RouteOptions = {
  replaceExisting?: boolean;
  guard?: RouteGuard;
};
